/* Brown Butter – Contact page form enhancements.
 * The form still posts natively to Shopify's /contact endpoint; this script only
 * adds inline validation, duplicate-submit protection and draft recovery.
 * Success is shown only when Shopify renders form.posted_successfully?. */
if (!customElements.get('bb-contact-form')) {
  const DRAFT_KEY = 'bb-contact-draft';
  const DRAFT_MAX_AGE = 30 * 60 * 1000;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const store = {
    get() {
      try {
        return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null');
      } catch (e) {
        return null;
      }
    },
    set(value) {
      try {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(value));
      } catch (e) {}
    },
    clear() {
      try {
        sessionStorage.removeItem(DRAFT_KEY);
      } catch (e) {}
    },
  };

  class BBContactForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submit = this.form.querySelector('[data-bb-submit]');
      this.summary = this.form.querySelector('[data-bb-summary]');
      this.submitLabel = this.submit ? this.submit.textContent.trim() : '';
      this.attempted = false;
      this.sending = false;

      this.form.setAttribute('novalidate', '');
      this.handleDraftOnLoad();

      this.submit?.addEventListener('click', (event) => {
        if (this.sending || !this.validate(true)) event.preventDefault();
      });
      // Capture phase so an invalid form is stopped before other submit listeners.
      this.form.addEventListener(
        'submit',
        (event) => {
          if (this.sending) return;
          if (!this.validate(true)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          this.startSending();
        },
        true
      );
      this.form.addEventListener('input', (event) => {
        if (this.attempted && event.target.matches('[required]')) this.validateField(event.target);
      });
      this.form.addEventListener('focusout', (event) => {
        if (this.attempted && event.target.matches('[required]')) this.validateField(event.target);
      });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.stopSending();
      });
    }

    get draftFields() {
      return Array.from(this.form.querySelectorAll('[data-bb-draft]'));
    }

    handleDraftOnLoad() {
      const success = this.form.querySelector('[data-bb-success]');
      const serverError = this.form.querySelector('[data-bb-server-error]');
      const draft = store.get();

      if (success) {
        store.clear();
        success.focus({ preventScroll: true });
        success.scrollIntoView({ block: 'center' });
        return;
      }

      const fresh = draft && draft.pending && Date.now() - draft.t < DRAFT_MAX_AGE;
      if (draft && !fresh) store.clear();

      if ((serverError || fresh) && draft) {
        let restored = false;
        this.draftFields.forEach((field) => {
          const saved = draft.fields[field.name];
          if (saved && !field.value.trim()) {
            field.value = saved;
            restored = true;
          }
        });
        const note = this.form.querySelector('[data-bb-restored]');
        if (restored && note && (serverError || draft.fields['contact[Comment]'])) note.hidden = false;
      }

      if (serverError) {
        serverError.focus({ preventScroll: true });
        serverError.scrollIntoView({ block: 'center' });
      }
    }

    saveDraft() {
      const fields = {};
      this.draftFields.forEach((field) => {
        fields[field.name] = field.value;
      });
      store.set({ fields, t: Date.now(), pending: true });
    }

    errorFor(field) {
      const value = field.value.trim();
      if (field.type === 'email') {
        if (!value) return field.dataset.bbMsgMissing;
        if (!EMAIL_RE.test(value) || (field.validity && field.validity.typeMismatch)) return field.dataset.bbMsgInvalid;
        return '';
      }
      if (field.required && !value) return field.dataset.bbMsgMissing;
      return '';
    }

    validateField(field) {
      const message = this.errorFor(field);
      const errorEl = this.form.querySelector(`#${field.id}-error`);
      if (message) {
        field.setAttribute('aria-invalid', 'true');
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
          field.setAttribute('aria-describedby', errorEl.id);
        }
      } else {
        field.removeAttribute('aria-invalid');
        field.removeAttribute('aria-describedby');
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
      }
      if (this.attempted) this.renderSummary();
      return !message;
    }

    validate(focusFirst) {
      this.attempted = true;
      const fields = Array.from(this.form.querySelectorAll('[required]'));
      const invalid = fields.filter((field) => !this.validateField(field));
      this.renderSummary();
      if (invalid.length && focusFirst) invalid[0].focus();
      return invalid.length === 0;
    }

    renderSummary() {
      if (!this.summary) return;
      const invalid = Array.from(this.form.querySelectorAll('[aria-invalid="true"]'));
      if (!invalid.length) {
        this.summary.hidden = true;
        this.summary.innerHTML = '';
        return;
      }
      const heading = this.form.closest('.bb-contact')?.dataset.errorHeading || '';
      const items = invalid
        .map((field) => {
          const text = this.errorFor(field) || '';
          return `<li><a href="#${field.id}">${text.replace(/</g, '&lt;')}</a></li>`;
        })
        .join('');
      this.summary.innerHTML =
        (heading ? `<p class="bb-contact__status-title">${heading.replace(/</g, '&lt;')}</p>` : '') +
        `<ul class="bb-contact__status-list" role="list">${items}</ul>`;
      this.summary.hidden = false;
    }

    startSending() {
      this.sending = true;
      this.saveDraft();
      this.form.setAttribute('aria-busy', 'true');
      if (this.submit) {
        this.submit.setAttribute('aria-disabled', 'true');
        this.submit.textContent = this.submit.dataset.bbSending || this.submitLabel;
        // Disable after the submit event so the native submission is not affected.
        setTimeout(() => {
          if (this.sending) this.submit.disabled = true;
        }, 0);
      }
      // If the page is still here much later (network failure, blocked captcha), allow a retry.
      this.retryTimer = setTimeout(() => this.stopSending(), 20000);
    }

    stopSending() {
      clearTimeout(this.retryTimer);
      this.sending = false;
      this.form.removeAttribute('aria-busy');
      if (this.submit) {
        this.submit.disabled = false;
        this.submit.removeAttribute('aria-disabled');
        this.submit.textContent = this.submitLabel;
      }
    }
  }

  customElements.define('bb-contact-form', BBContactForm);
}
